import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
    getTourSteps,
    useTourFeatures,
} from '@/components/common/OnboardingTour';
import './TourResumeBanner.css';

/**
 * Banner shown on non-dashboard pages when the user has an incomplete tour.
 * Shows progress percentage and a button to return to the dashboard to continue.
 */
export default function TourResumeBanner() {
    const { userProfile } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Read before the early returns below (hooks run on every render). The
    // step count has to match the tour's, which drops flagged-off steps.
    const tourFeatures = useTourFeatures();

    // Only show on non-dashboard pages
    if (location.pathname === '/dashboard') return null;

    // Only show if the user has a saved step (meaning they started but didn't finish)
    if (!userProfile || userProfile.hasSeenTour) return null;
    if (!userProfile.tourStep || userProfile.tourStep <= 0) return null;

    const stepCount = getTourSteps(
        userProfile.roles || [],
        tourFeatures
    ).length;
    const progress = Math.round((userProfile.tourStep / stepCount) * 100);

    return (
        <Alert
            variant="info"
            className="mb-0 rounded-0 border-start-0 border-end-0 py-2"
        >
            <div className="container d-flex align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3 flex-grow-1">
                    <span className="fw-medium small">
                        Platform tour {progress}% complete
                    </span>
                    <div
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Tour ${progress}% complete`}
                        className="progress flex-grow-1 tour-resume-progress"
                    >
                        <div
                            className="progress-bar bg-info"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
                <Button
                    variant="info"
                    size="sm"
                    className="rounded-pill"
                    onClick={() => navigate('/dashboard')}
                >
                    Continue Tour
                </Button>
            </div>
        </Alert>
    );
}
